/**
 * Where an aeroplane went in, and what is left there — AD-014c.
 *
 * A port extension, stated up front the way `scenery-fires.ts` states its own.
 * The original does not do this: `damage.c`'s kind-6 path at `0x1dd19` calls
 * `kill_object(obj)` into `pyro.c` and seeds the object with two random tumble
 * rates (`collisions-design.md` §4), and `pyro.c` is an *explosion* routine —
 * the bomb path likewise "retires the object and calls `pyro.c+0xa8` for the
 * explosion" (`docs/game/ground-traffic.md`). Nothing anywhere in the recovered
 * record describes persistent wreckage, a scorch, or a fire left burning on the
 * ground, and `pyro.c` has never been disassembled, so this is declared an
 * enhancement rather than a reconstruction. The *visual* therefore sits behind
 * ENHANCED EFFECTS on the same replace-nothing terms AD-018's burning buildings
 * do; the record below exists either way, because a rendering toggle must never
 * move the encounter hash.
 *
 * ## Shape
 *
 * Sparse in the crashes, not dense in the roster — AD-018's rule. One record
 * per aeroplane that actually reached the ground, never one per aeroplane, and
 * `undefined` rather than `[]` in the hash snapshot so an encounter in which
 * nobody has gone in yet serializes to exactly the string it did before this
 * field existed.
 *
 * ## Not an `EncounterTarget`
 *
 * The same refusal `scenery-fires.ts` makes, and for a sharper reason: a target
 * is something `pickQuarry` can choose and `gun-pass.ts` can sweep. A burning
 * hole in a field is neither. It has no side worth defending, nothing left to
 * destroy, and an AI that flew down to strafe one would be doing something no
 * pilot has ever done.
 */

import { headingAngle, type Quat } from "../flight/math3d.ts";
import type { AircraftKey } from "../flight/aircraft-table.ts";

/**
 * One aeroplane's arrival. Eight numbers and two strings, which is the whole
 * cost of the feature in state.
 */
export interface CrashSite {
  /** The aeroplane that went in. An aeroplane crashes once, so this is also the record's identity. */
  readonly id: string;
  /** Which airframe — a renderer sizes the scar by it, and a debrief can name it. */
  readonly aircraft: AircraftKey;
  /** Impact point, world metres. `y` is the **terrain** height under it, sampled from the encounter's own `GroundQuery`. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /**
   * The heading the airframe was on when it arrived, radians. Wreckage does not
   * scatter in a circle — it goes the way the aeroplane was going — so the mark
   * is a streak with a direction rather than a disc.
   */
  readonly headingRad: number;
  /** Seconds since the impact. Everything the renderer does with a site is a function of this. */
  readonly ageSeconds: number;
  /**
   * Whether there is a fire to draw. False for the wreck that mushes in — an
   * out-of-control aeroplane that arrives gently is `destroyed` too
   * (`outcome.ts`), and it leaves a broken aeroplane in a field, not a pyre.
   */
  readonly burning: boolean;
}

/**
 * Every number the aftermath is made of. **All CALIBRATION-PENDING** and all
 * invented, because there is no such mechanic in the original to have measured
 * anything from.
 */
export const CRASH_TUNABLES = {
  /**
   * Seconds of open flame. A wooden-and-fabric aeroplane with a punctured tank
   * is not a long fire; this is long enough that a pilot who kills someone and
   * then turns to re-engage can still find the pyre when he next looks down,
   * and short enough that a four-kill sortie does not leave the map alight.
   */
  flameSeconds: 45,
  /**
   * Seconds the column keeps rising after the flame is out. Twice the fire,
   * because that is the shape of a burnt-out wreck: the smoke is what a pilot
   * actually navigates by, and every period account of a crowded sector
   * describes the smoke rather than the flames.
   */
  smokeSeconds: 90,
  /**
   * Metres across, before the airframe's own size scales it. The mark is a
   * streak roughly a wingspan wide and a few longer, which is what a scout
   * arriving nose-first at forty metres a second actually leaves.
   */
  markSpreadM: 9,
} as const;

/**
 * Sites tracked at once. Six, matching `MAX_SCENERY_FIRES` and for the same
 * reason: it is a cap on *records*, the roster tops out at nine aeroplanes, and
 * a sortie in which seven of them reach the ground is not a sortie anyone has
 * flown. Beyond the cap a new site is simply not recorded — a deliberate
 * refusal rather than an eviction, because an eviction needs a policy and a
 * policy can be got wrong, and the failure it would prevent is "the seventh
 * aeroplane you shot down left no scorch".
 */
export const MAX_CRASH_SITES = 6;

export interface CrashArrival {
  readonly id: string;
  readonly aircraft: AircraftKey;
  /** Where the airframe stopped, horizontally. */
  readonly x: number;
  readonly z: number;
  /**
   * Terrain height under that point, from the encounter's own `GroundQuery` —
   * the same query `applyGround` put the wheels on and the shadow drapes to.
   * Passed in rather than sampled here so this module stays free of the seam.
   */
  readonly groundY: number;
  readonly orientation: Quat;
  /** True when it arrived as a crash or was already alight. */
  readonly burning: boolean;
}

/**
 * Records one arrival. Returns the list unchanged when the aeroplane already
 * has a site — an aeroplane arrives once, and `destroyed` is terminal — or when
 * the cap refuses it.
 */
export function recordCrash(
  sites: readonly CrashSite[],
  arrival: CrashArrival,
): readonly CrashSite[] {
  if (sites.length >= MAX_CRASH_SITES) return sites;
  if (sites.some((site) => site.id === arrival.id)) return sites;
  return [
    ...sites,
    {
      id: arrival.id,
      aircraft: arrival.aircraft,
      x: arrival.x,
      y: arrival.groundY,
      z: arrival.z,
      headingRad: headingAngle(arrival.orientation),
      ageSeconds: 0,
      burning: arrival.burning,
    },
  ];
}

/**
 * Ages every site by one tick. Sites are never dropped: the scar is the one
 * part of this that outlives its own fire, and a pilot who comes back over the
 * spot ten minutes later should still find it.
 */
export function advanceCrashSites(sites: readonly CrashSite[], dt: number): readonly CrashSite[] {
  if (sites.length === 0) return sites;
  return sites.map((site) => ({ ...site, ageSeconds: site.ageSeconds + dt }));
}

/** 1 while the flame is at full height, falling to 0 as it goes out. Zero for a wreck that never caught. */
export function crashFlameIntensity(site: CrashSite): number {
  if (!site.burning) return 0;
  const spent = site.ageSeconds / CRASH_TUNABLES.flameSeconds;
  return spent >= 1 ? 0 : 1 - spent;
}

/**
 * 1 while the column is at full rate, falling to 0 as it thins out. A site that
 * never caught still smokes a little — a broken aeroplane with a hot engine in
 * it does — so this is not gated on `burning` the way the flame is; it is
 * merely much weaker.
 */
export function crashSmokeIntensity(site: CrashSite): number {
  const spent = site.ageSeconds / CRASH_TUNABLES.smokeSeconds;
  if (spent >= 1) return 0;
  return (1 - spent) * (site.burning ? 1 : 0.25);
}
